import React, {useState, useEffect, useCallback, useMemo, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import styled from 'styled-components';
import EllipsesControl from '../EllipsesControl/EllipsesControl';
import Modal from '../Modal/Modal';
import RelateItemList from '../RelateList/RelateItemList';
import {labels, currentResource} from '../../../util/utils';
import {extendedPagingOptions, modalLevelToast} from '../../../util/uiconstants';
import Loader from '../../Common/LoadingIndicator/LoadingIndicator';
import {useDocumentId, useEngagementId} from '../../../util/customHooks';
import {getScotById} from '../../../actions/scots/scotsactions';
import {
	relateWCGWToFlowChartStep,
	unrelateWCGWFromFlowChartStep,
	updateStoreRelatedFlowchartStepWCGW,
	updateStoreUnrelatedWCGWFromFlowchartStep
} from '../../../actions/FlowChart/flowchartstepactions';
import {clearErrors} from '../../../actions/erroractions';
import {setFormV2ToasterVisibility} from '../../../actions/toastactions';

export default function RelateFlowchartWCGWModal({isModalOpen, onClickRelateModal, stepName}) {
	const dispatch = useDispatch();
	const [currentPage, setCurrentPage] = useState(1);
	const wcgws = useSelector((state) => state.scotsDetails.wcgWs);
	const entityUIDInt = useSelector((state) => state.flowChart?.data[0].entityUIDInt);
	const flowchartWCGWRef = useRef(
		new Set(
			useSelector((state) => {
				const wcgwSet = (state.flowchartStep.flowchartStepsWCGW || [])?.map((wcgwItem) => wcgwItem.wcgwId);
				return wcgwSet;
			})
		)
	);

	const flowchartId = useSelector((state) => state.flowChart.data[0].id);
	const flowchartStepId = useSelector((state) => state.flowchartStep.id);
	const [isLoading, setIsLoading] = useState(true);

	const engagementId = useEngagementId();
	const documentId = useDocumentId();

	useEffect(() => {
		dispatch(clearErrors());
		dispatch(setFormV2ToasterVisibility(false));
	}, []);

	useEffect(() => {
		setIsLoading(true);
		dispatch(getScotById(engagementId, entityUIDInt, {includeWCGW: 'true'})).finally(() => {
			setIsLoading(false);
		});
	}, [dispatch, engagementId, entityUIDInt]);

	const renderItemComponent = useCallback((wcgw) => {
		return <EllipsesControl noOfLines={1} content={wcgw.wcgwName} tooltip={wcgw.wcgwName} />;
	}, []);

	const selected = useCallback(
		(entity) => {
			return flowchartWCGWRef.current.has(entity.id);
		},
		[flowchartWCGWRef]
	);

	const componentHeader = useCallback(() => {
		return (
			<section className="filter-wcgw-container">
				<section className="related-wcgw">
					<EllipsesControl noOfLines={1} content={stepName} tooltip={stepName} />
				</section>
			</section>
		);
	}, []);

	const relateListHeaders = useCallback(
		(totalEntityCount) => (
			<section className="wcgw-header">
				<section className="wcgw-name">{labels.flowChartNewSteps.headerNameForWCGW}</section>
				<section className="wcgw-count">
					{totalEntityCount > 0 && (
						<section>
							{`${totalEntityCount > 1 ? labels.flowChartWCGW.wcgwsCounter : labels.flowChartWCGW.wcgwCounter}`.replace(
								'{0}',
								totalEntityCount
							)}
						</section>
					)}
				</section>
			</section>
		),
		[]
	);

	const onCheckChange = useCallback(
		(wcgwItem, checked) => {
			let promise = null;
			if (checked) {
				let wcgwData = {
					wcgwId: wcgwItem.id,
					documentId: documentId
				};
				promise = dispatch(relateWCGWToFlowChartStep(engagementId, flowchartId, flowchartStepId, wcgwData)).then(() => {
					flowchartWCGWRef.current.add(wcgwItem.id);
					dispatch(
						updateStoreRelatedFlowchartStepWCGW({
							wcgwName: wcgwItem.wcgwName,
							engagementId: engagementId,
							flowchartStepId: flowchartStepId,
							wcgwId: wcgwItem.id,
							remove: {}
						})
					);
				});
			} else {
				promise = dispatch(unrelateWCGWFromFlowChartStep(engagementId, flowchartId, flowchartStepId, wcgwItem.id)).then(
					() => {
						flowchartWCGWRef.current.delete(wcgwItem.id);
						dispatch(
							updateStoreUnrelatedWCGWFromFlowchartStep({
								flowchartStepId: flowchartStepId,
								wcgwId: wcgwItem.id
							})
						);
					}
				);
			}
			return promise;
		},
		[dispatch, engagementId, flowchartId, flowchartStepId, documentId, flowchartWCGWRef]
	);

	let configuration = useMemo(
		() => ({
			noResultsLabel: labels.noResultsFound,
			data: wcgws,
			selected: (entity) => selected(entity),
			filter: (item, searchString) => {
				setCurrentPage(1);
				return item.wcgwName.toLowerCase().includes(searchString.toLowerCase());
			},
			render: renderItemComponent,
			onCheckBoxChange: onCheckChange,
			noDataLabel: labels.noResultsFound
		}),
		[wcgws, selected, onCheckChange, renderItemComponent]
	);

	return (
		<Modal
			modalsize="medium"
			show={isModalOpen}
			title={labels.flowChartWCGW.headerName}
			onHide={onClickRelateModal}
			closeTitle={labels.cancelLabel}
			showFooter={false}
			modalLevel={modalLevelToast.level2}
		>
			<StyledSection>
				{isLoading ? (
					<div className="loading-indicator">
						<Loader show={isLoading} />
					</div>
				) : (
					<RelateItemList
						key={`relateitemlist-${wcgws?.length}`}
						componentHeader={componentHeader}
						listHeaders={relateListHeaders}
						page={currentPage}
						onPageChanged={(newPage) => {
							if (newPage && newPage !== currentPage) {
								setCurrentPage(newPage);
							}
						}}
						onPageSizeChanged={() => {
							setCurrentPage(1);
						}}
						search
						pagination
						optionsPerPage={extendedPagingOptions.options}
						configuration={configuration}
						showAddedLabel
						addedLabel={labels.added}
						searchPlaceholder={labels.searchPlaceholder}
						paginationIconHover={currentResource.pagingSvgHoverText}
						paginationShowText={labels.pagingShowtext}
						showSelectedOnlyCheckBox
						showSelectedOnlyText={labels.flowChartWCGW.showOnlyRelatedText}
						onFilter={() => {
							setCurrentPage(1);
						}}
					/>
				)}
			</StyledSection>
		</Modal>
	);
}

const StyledSection = styled.section`
	height: 100%;
	/* .relate-list-toolbar {
		display: block;
		.additional-toolbar-controls {
			width: 100%;
			max-width: 100%;
			margin-left: 0;
			display: inline-flex;
			.motif-search-input-wrapper {
				width: var(--px-400);
			}
			.motif-checkbox-wrapper {
				margin-left: auto;
			}
		}
		.StyledPagination {
			display: flex;
			justify-content: end;
			flex: 1;
			padding: var(--px-10) 0;
		}
	} */

	.relate-list-body-wrapper {
		.relate-item-list-labelTxt {
			width: 85%;
			max-width: 85%;
		}
	}

	.RelateItemsListHeaderSelectedOnly {
		max-width: 100%;
		display: flex;
		align-items: center;
		.RelateItemsListHeaderSelectedOnlyCheckbox {
			margin: var(--px-10) 0;
			max-width: 100%;
		}
		.RelateItemsListHeaderSelectedOnlyCheckbox {
			.motif-checkbox-label {
				width: calc(100% - var(--px-20));
			}
		}
	}
	.RelateItemsListHeaderComponent {
		width: 100% !important;
		max-width: 80%;
	}
	.filter-wcgw-container {
		width: 100%;
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: var(--px-10) 0;
		.related-wcgw {
			display: flex;
			width: 100%;
			font-size: var(--px-14);
			font-weight: 300;
			color: var(--neutrals-900);

			.title {
				margin-right: var(--px-4);
				font-size: var(--px-14);
				font-weight: 300;
			}

			.ellipses {
				font-size: var(--px-14);
				font-weight: 400;
				width: 92%;
			}
		}
	}
	.wcgw-name {
		margin-left: var(--px-33);
		font-weight: bold;
	}
	.wcgw-count {
		margin-left: auto;
		margin-right: var(--px-20);
		min-width: var(--px-100);
		text-align: right;
	}
	.wcgw-header {
		display: flex;
	}
	.motif-checkbox {
		display: flex;
		justify-content: flex-end;
		width: 100%;
	}
	.loading-indicator {
		display: flex;
		justify-content: center;
		align-items: center;
		height: 100%;
	}
`;
